import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Bell, Calendar, Pill, MessageSquare, FileText, Info, Check } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const typeConfig = {
  appointment_reminder: { icon: Calendar, color: "bg-blue-50 text-blue-600" },
  refill_update: { icon: Pill, color: "bg-amber-50 text-amber-600" },
  new_message: { icon: MessageSquare, color: "bg-green-50 text-green-600" },
  form_due: { icon: FileText, color: "bg-purple-50 text-purple-600" },
  general: { icon: Info, color: "bg-gray-50 text-gray-600" },
};

export default function Notifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);

  const fetchNotifs = () => { API.get("/notifications").then(({ data }) => setNotifications(data || [])).catch(() => {}); };
  useEffect(() => { if (user) fetchNotifs(); }, [user]);

  const unread = notifications.filter(n => !n.is_read);

  const handleMarkAllRead = async () => {
    await API.post("/notifications/mark-all-read");
    fetchNotifs();
  };

  const handleMarkRead = async (notif) => {
    if (!notif.is_read) {
      await API.put(`/notifications/${notif.id}`, { is_read: true });
      fetchNotifs();
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6 animate-fade-in" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <PageHeader title="Notifications" subtitle={`${unread.length} unread`} />
      </div>
      {unread.length > 0 && (
        <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs gap-1.5 mb-4" data-testid="mark-all-read-button">
          <Check className="w-3.5 h-3.5" />Mark all read
        </Button>
      )}
      {notifications.length === 0 ? <EmptyState icon={Bell} title="No notifications" description="You're all caught up! Notifications will appear here." /> : (
        <div className="space-y-2">
          {notifications.map((notif) => {
            const config = typeConfig[notif.type] || typeConfig.general;
            const Icon = config.icon;
            const content = (
              <div className={`flex items-start gap-3 p-3.5 rounded-xl transition-colors cursor-pointer ${notif.is_read ? "bg-card" : "bg-accent/70"} border border-border hover:border-primary/10`}
                onClick={() => handleMarkRead(notif)} data-testid="notification-item">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${config.color}`}><Icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className={`text-sm ${notif.is_read ? "font-medium" : "font-semibold"}`}>{notif.title}</h4>
                    {!notif.is_read && <div className="w-2 h-2 rounded-full bg-secondary shrink-0 mt-1.5" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{notif.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{notif.created_date ? new Date(notif.created_date).toLocaleDateString() : ""}</p>
                </div>
              </div>
            );
            return notif.link_to ? <Link key={notif.id} to={notif.link_to} className="block">{content}</Link> : <div key={notif.id}>{content}</div>;
          })}
        </div>
      )}
    </div>
  );
}
